import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, GitBranch } from "lucide-react";
import { toast } from "sonner";
import { RISK_COLORS, RISK_LABELS } from "@/lib/agents";

export default function Decisions() {
  const [items, setItems] = useState([]);
  const [dlg, setDlg] = useState(false);
  const [form, setForm] = useState({ risk: "medium", agents_involved: "" });

  const load = () => api.get("/decisions").then((r) => setItems(r.data.decisions));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.title || !form.decision) return toast.error("Título y decisión requeridos");
    await api.post("/decisions", {
      title: form.title, context: form.context || "", decision: form.decision,
      rationale: form.rationale || "", risk: form.risk,
      agents_involved: form.agents_involved.split(",").map((s) => s.trim()).filter(Boolean),
    });
    toast.success("Decisión registrada");
    setForm({ risk: "medium", agents_involved: "" });
    setDlg(false);
    load();
  };

  return (
    <div className="p-8 space-y-6" data-testid="decisions-page">
      <div className="border-b border-[var(--border)] pb-5 flex items-end justify-between">
        <div>
          <div className="label-mono mb-2">DECISION LOG</div>
          <h1 className="font-display font-black text-4xl tracking-tight">Decisiones</h1>
          <p className="text-sm text-[var(--text-dim)] mt-1">
            Cada decisión importante queda registrada con contexto, ratio y agentes involucrados.
          </p>
        </div>
        <Dialog open={dlg} onOpenChange={setDlg}>
          <DialogTrigger asChild>
            <Button className="rounded-none bg-[var(--accent)] text-[var(--bg)] hover:bg-[var(--accent)]/80" data-testid="dec-add">
              <Plus className="w-4 h-4 mr-2" /> Registrar decisión
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-none bg-[var(--surface)] border-[var(--border)] max-w-xl">
            <DialogHeader><DialogTitle className="font-display">Registrar decisión</DialogTitle></DialogHeader>
            <div className="space-y-3">
              {["title","context","decision","rationale"].map((k) => (
                <div key={k}>
                  <Label className="label-mono">{k}</Label>
                  <Textarea value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                            className="rounded-none bg-[var(--bg)] border-[var(--border)] font-mono min-h-[70px]" data-testid={`dec-${k}`} />
                </div>
              ))}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="label-mono">Riesgo</Label>
                  <Select value={form.risk} onValueChange={(v) => setForm({ ...form, risk: v })}>
                    <SelectTrigger className="rounded-none bg-[var(--bg)] border-[var(--border)] font-mono" data-testid="dec-risk"><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-none bg-[var(--surface)] border-[var(--border)]">
                      {["low","medium","high","critical"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="label-mono">Agentes (coma)</Label>
                  <Input value={form.agents_involved} onChange={(e) => setForm({ ...form, agents_involved: e.target.value })}
                         placeholder="ceo, cfo, ciso" className="rounded-none bg-[var(--bg)] border-[var(--border)] font-mono" data-testid="dec-agents" />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={create} className="rounded-none bg-[var(--accent)] text-[var(--bg)] hover:bg-[var(--accent)]/80" data-testid="dec-submit">
                Registrar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {items.length === 0 ? (
        <div className="border border-dashed border-[var(--border)] p-16 text-center" data-testid="dec-empty">
          <GitBranch className="w-8 h-8 mx-auto text-[var(--text-dim)] mb-3" />
          <p className="text-sm text-[var(--text-dim)]">Aún no hay decisiones registradas.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((d) => (
            <div key={d.id} className="border border-[var(--border)] bg-[var(--surface)] p-5" data-testid={`dec-${d.id}`}>
              <div className="flex items-center gap-2 mb-2">
                <Badge className="rounded-none font-mono text-[10px]" style={{ background: RISK_COLORS[d.risk], color: "#0B0E14" }}>
                  {RISK_LABELS[d.risk] || d.risk}
                </Badge>
                <span className="font-mono text-[10px] text-[var(--text-dim)]">
                  {new Date(d.created_at).toLocaleString("es-ES")}
                </span>
              </div>
              <div className="font-display font-bold text-lg mb-2">{d.title}</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                <Field label="Contexto" v={d.context} />
                <Field label="Decisión" v={d.decision} accent />
                <Field label="Rationale" v={d.rationale} />
              </div>
              {d.agents_involved?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1">
                  {d.agents_involved.map((a) => (
                    <span key={a} className="font-mono text-[10px] text-[var(--accent)] uppercase">#{a}</span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const Field = ({ label, v, accent }) => (
  <div className="border border-[var(--border)] bg-[var(--bg)] p-3">
    <div className="label-mono mb-1">{label}</div>
    <div className={`text-xs leading-relaxed whitespace-pre-wrap ${accent ? "text-[var(--accent)]" : "text-[var(--text-dim)]"}`}>
      {v || "—"}
    </div>
  </div>
);
